import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { UserManagement } from "@/components/UserManagement";
import { ChangePassword } from "@/components/ChangePassword";

export default function Settings() {
  const { user, isAdmin } = useAuth();
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/settings/company").then((r) => setS(r.data)); }, []);

  const save = async () => {
    try {
      await api.put("/settings/company", s);
      toast.success("Ayarlar kaydedildi");
    } catch { toast.error("Kaydedilemedi"); }
  };

  if (!s) return <div className="text-center py-10 text-muted-foreground">Yükleniyor...</div>;

  const F = ({ label, k, type = "text", rows }) => (
    <div>
      <Label>{label}</Label>
      {rows ? (
        <Textarea rows={rows} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="mt-1.5" data-testid={`setting-${k}`} />
      ) : (
        <Input type={type} value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: type === "number" ? Number(e.target.value) : e.target.value })} className="mt-1.5" data-testid={`setting-${k}`} />
      )}
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">{isAdmin ? "Ayarlar" : "Hesabım"}</h1>
          <p className="text-sm text-muted-foreground">{isAdmin ? "Firma bilgileri ve belge şablonları" : "Hesap bilgileri ve şifre"}</p>
        </div>
        {isAdmin && <Button onClick={save} data-testid="settings-save-btn"><Save size={16} className="mr-1" /> Kaydet</Button>}
      </div>

      <Tabs defaultValue={isAdmin ? "company" : "user"}>
        <TabsList>
          {isAdmin && <TabsTrigger value="company">Firma Bilgileri</TabsTrigger>}
          {isAdmin && <TabsTrigger value="documents">Teklif / Belge Ayarları</TabsTrigger>}
          <TabsTrigger value="user" data-testid="account-tab">Kullanıcı</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" data-testid="users-tab">Kullanıcılar</TabsTrigger>}
        </TabsList>

        <TabsContent value="company" className="mt-4">
          <Card className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Firma Adı" k="name" />
              <F label="Slogan" k="slogan" />
              <F label="Telefon" k="phone" />
              <F label="E-posta" k="email" />
              <F label="Web Sitesi" k="website" />
              <F label="Logo URL" k="logo_url" />
              <F label="Vergi Dairesi" k="tax_office" />
              <F label="Vergi Numarası" k="tax_no" />
              <F label="IBAN" k="iban" />
            </div>
            <F label="Adres" k="address" rows={2} />
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <Card className="p-6 space-y-4">
            <label className="flex items-center justify-between gap-4 p-3 rounded-md border border-border">
              <div>
                <div className="font-semibold">KDV ve İskonto Kullan</div>
                <div className="text-sm text-muted-foreground">Açıkken satış ekranında her kalemde KDV oranı ve iskonto alanları görünür.</div>
              </div>
              <input type="checkbox" className="h-5 w-5" checked={!!s.use_vat_discount} onChange={(e) => setS({ ...s, use_vat_discount: e.target.checked })} data-testid="setting-use-vat-discount" />
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <F label="Varsayılan KDV (%)" k="default_vat" type="number" />
              <F label="Teklif Geçerlilik (gün)" k="quote_validity_days" type="number" />
              <F label="Teklif No Öneki" k="quote_prefix" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <F label="Fatura No Öneki" k="sale_prefix" />
            </div>
            <F label="Varsayılan Ödeme Şartları" k="default_payment_terms" rows={2} />
            <F label="Varsayılan Teslimat Şartları" k="default_delivery_terms" rows={2} />
            <F label="Varsayılan Teklif Şartları" k="default_quote_terms" rows={2} />
            <F label="PDF Alt Bilgi" k="footer_text" rows={2} />
          </Card>
        </TabsContent>

        <TabsContent value="user" className="mt-4 space-y-4">
          <Card className="p-6 space-y-3">
            <div><Label>Ad Soyad</Label><Input value={user?.name || ""} disabled className="mt-1.5" /></div>
            <div><Label>E-posta</Label><Input value={user?.email || ""} disabled className="mt-1.5" /></div>
            <div><Label>Rol</Label><Input value={user?.role === "admin" ? "Yönetici" : "Personel"} disabled className="mt-1.5" /></div>
          </Card>
          <ChangePassword />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="users" className="mt-4"><UserManagement currentUser={user} /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}
